// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

// import utility file
import { Col, Image, Row, Table } from 'react-bootstrap';
// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import EleveService from '../../services/EleveService';
import SalledeclasseService from '../../services/SalledeclasseService';
import SalledeclasseProfileLayout from './SalledeclasseProfileLayout';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import Pagination from 'components/elements/advance-table/Pagination';
import AnneescolaireService from '../../services/AnneescolaireService';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ViewEleves = () => {
	const { id } = useParams();
	const [eleves, setEleves] = useState([]);
	const [salledeclasse, setSalledeclasse] = useState(null);
	const [currentSchoolYear, setCurrentSchoolYear] = useState(null);

	useEffect(() => {
		const fetchCurrentSchoolYear = async () => {
			try {
				const schoolYear = await AnneescolaireService.getCurrentAnneescolaire();
				setCurrentSchoolYear(schoolYear.data);

				// Maintenant, appelez fetchEleves ici après avoir défini currentSchoolYear
				await fetchEleves(schoolYear.data.id);
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'année scolaire en cours:",
					error
				);
				alert(
					`Erreur lors de la récupération de l'année scolaire en cours. Détails de l'erreur : ${error.message}`
				);
			}
		};

		const fetchEleves = async (anneeScolaireId) => {
			try {
				const response = await EleveService.getAllEleves(parseInt(id));
				// filter eleves who belong to the current class
				const filteredEleves = response.filter(
					(eleve) => eleve.anneescolaire === anneeScolaireId
				);
				setEleves(filteredEleves);
			} catch (error) {
				console.error('Error fetching eleves data', error);
			}
		};

		fetchCurrentSchoolYear();
	}, [id]); // Assurez-vous que 'id' est une dépendance à jour

	useEffect(() => {
		const fetchSalledeclasse = async () => {
			try {
				const response = await SalledeclasseService.getSalledeclasse(id);
				setSalledeclasse(response.data);
			} catch (error) {
				console.error('Error fetching salle de classe data', error);
			}
		};

		fetchSalledeclasse();
	}, [id]);

	const formatIntituleClasse = (value) => {
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else if (
				columnId === 'genre' ||
				(columnId === 'statut_eleve' && value)
			) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else if (
				columnId === 'lieu_naissance' ||
				(columnId === 'prenom' && value)
			) {
				return value.replace(/[^-'\s]+/g, function (word) {
					return word.replace(/^./, function (first) {
						return first.toUpperCase();
					});
				});
			} else {
				return value;
			}
		} else if (columnId === 'salledeclasse') {
			return value && value.intitule_classe
				? formatIntituleClasse(value.intitule_classe).toUpperCase()
				: ''; // Return the intitule_classe property
		}
		return value;
	};

	const getInitials = (eleve) => {
		const firstName = eleve.prenom ? eleve.prenom.charAt(0).toUpperCase() : '';
		const lastName = eleve.nom ? eleve.nom.charAt(0).toUpperCase() : '';
		return `${lastName} ${firstName}`;
	};

	const formatStatutEleve = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					const imageUrl = `${backendUrl}${row.original.photo}`;
					return (
						<div className="d-flex align-items-center">
							{row.original.photo ? (
								<Image
									src={imageUrl}
									alt=""
									className="rounded-circle avatar-md"
								/>
							) : (
								<Avatar
									type="initial"
									size="md"
									name={initials}
									className="rounded-circle"
									alt={initials}
								/>
							)}
							{value ? (
								<Link to={`/education/eleves/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'prenom',
				Header: 'Prenom',
				Cell: ({ value }) => <div>{formatValue(value, 'prenom')}</div>
			},
			{ accessor: 'date_naissance', Header: 'Date de naissance' },
			{
				accessor: 'lieu_naissance',
				Header: 'Lieu de naissance',
				Cell: ({ value }) => <div>{formatValue(value, 'lieu_naissance')}</div>
			},
			{
				accessor: 'genre',
				Header: 'Genre',
				Cell: ({ value }) => <div>{formatValue(value, 'genre')}</div>
			},
			{
				accessor: 'statut_eleve',
				Header: "Statut de l'élève",
				Cell: ({ value }) => <div>{formatStatutEleve(value)}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return eleves.map((eleve) => ({
			...eleve
		}));
	}, [eleves]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex } = state;

	return (
		<SalledeclasseProfileLayout>
			<Row>
				{eleves.length > 0 ? (
					<>
						<Table
							hover
							responsive
							{...getTableProps()}
							className="table text-nowrap table-centered"
						>
							<thead className="table-primary">
								{headerGroups.map((headerGroup) => (
									<tr {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column) => (
											<th {...column.getHeaderProps()}>
												{column.render('Header')}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()}>
								{page.map((row) => {
									prepareRow(row);
									return (
										<tr {...row.getRowProps()}>
											{row.cells.map((cell) => (
												<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
											))}
										</tr>
									);
								})}
							</tbody>
						</Table>

						<Pagination
							previousPage={previousPage}
							pageCount={pageCount}
							pageIndex={pageIndex}
							gotoPage={gotoPage}
							nextPage={nextPage}
						/>
					</>
				) : (
					<Col>Aucun élève dans cette salle de classe.</Col>
				)}
			</Row>
		</SalledeclasseProfileLayout>
	);
};

export default ViewEleves;
